import React, { useState } from 'react';
import { AppBar, Toolbar, Box, Typography, InputBase, IconButton, Badge, useMediaQuery } from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ThemeToggleButton from '../../ThemeToggleButton';

// --- Styled Components for the Search Bar ---
const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: alpha(theme.palette.background.default, 0.08),
    '&:hover': {
        backgroundColor: alpha(theme.palette.background.default, 0.12),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '30ch',
        },
    },
}));
// --- End of Styled Components ---

export default function Header() {
    const { isAuthenticated } = useSelector((state) => state.user);
    const { cartItems } = useSelector((state) => state.cart);
    const { wishlistItems } = useSelector((state) => state.wishlist);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showSearch, setShowSearch] = useState(false);

    const handleSearch = (e) => {
        if (e.key === 'Enter' && e.target.value) {
            navigate(`/search/${e.target.value}`);
        }
    };

    return (
        <AppBar
            position="sticky"
            sx={{
                background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(26,26,34,0.85)'
                    : 'linear-gradient(90deg, #e3f0ff 0%, #f5f7fa 100%)', // subtle blue/gray tint for light mode
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? `0 4px 24px ${alpha(theme.palette.primary.main, 0.08)}`
                    : `0 2px 12px ${alpha(theme.palette.grey[500], 0.10)}`,
                borderBottom: (theme) => theme.palette.mode === 'dark'
                    ? `1.5px solid ${alpha(theme.palette.primary.main, 0.10)}`
                    : `1.5px solid ${alpha(theme.palette.grey[300], 0.18)}`,
                zIndex: 1201,
            }}
        >
            <Toolbar sx={{ minHeight: { xs: 56, sm: 72 }, px: { xs: 1, sm: 3 } }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mr: 3 }}>
                    <Typography
                        variant="h6"
                        noWrap
                        component={Link}
                        to="/"
                        sx={{
                            color: (theme) => theme.palette.text.primary,
                            textDecoration: 'none',
                            fontWeight: 700,
                            letterSpacing: 1.5,
                            fontSize: { xs: 22, sm: 26 },
                            lineHeight: 1.1,
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'scale(1.07) translateY(-2px)',
                                textShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 2px 8px #00e5ff'
                                    : '0 2px 8px #1976d2',
                            },
                        }}
                    >
                        <img src="/logo.png" alt="Logo" style={{ width: isMobile ? 80 : 100, display: 'block' }} />
                    </Typography>
                    <Box sx={{
                        width: isMobile ? 80 : 100,
                        height: 3,
                        borderRadius: 2,
                        mt: 0.5,
                        background: (theme) => theme.palette.mode === 'dark'
                            ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                            : `linear-gradient(90deg, #1976d2, #00bcd4)`,
                        opacity: 0.7,
                    }} />
                </Box>

                {isMobile ? (
                    <>
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton color="inherit" onClick={() => setShowSearch(!showSearch)}>
                            <SearchIcon />
                        </IconButton>
                    </>
                ) : (
                    <Search sx={{
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 2px 8px rgba(0,229,255,0.08)'
                            : '0 2px 8px rgba(25,118,210,0.04)',
                        border: (theme) => theme.palette.mode === 'dark'
                            ? `1.5px solid ${alpha(theme.palette.primary.main, 0.10)}`
                            : `1.5px solid ${alpha(theme.palette.grey[400], 0.25)}`,
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.default, 0.08)
                            : 'rgba(240, 245, 255, 0.95)',
                        transition: 'box-shadow 0.2s, border 0.2s',
                    }}>
                        <SearchIconWrapper>
                            <SearchIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Search for products, brands and more"
                            inputProps={{ 'aria-label': 'search', style: { color: 'inherit' } }}
                            sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#1a237e' }}
                            onKeyDown={handleSearch}
                        />
                    </Search>
                )}

                {!isMobile && <Box sx={{ flexGrow: 1 }} />}

                {/* Action Icons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1.5 } }}>
                    <ThemeToggleButton />
                    <IconButton
                        size="large"
                        component={Link}
                        to="/wishlist"
                        aria-label={`show ${wishlistItems.length} wishlist items`}
                        sx={{
                            color: (theme) => theme.palette.text.primary,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                ? '0 2px 8px rgba(0,229,255,0.10)'
                                : '0 2px 8px rgba(25,118,210,0.06)',
                            '&:hover': {
                                transform: 'translateY(-2px) scale(1.08)',
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 4px 16px #00e5ff44'
                                    : '0 4px 16px #1976d244',
                            },
                        }}
                    >
                        <Badge badgeContent={wishlistItems.length} color="secondary" sx={{
                            '& .MuiBadge-badge': {
                                fontWeight: 600,
                                fontSize: 13,
                                boxShadow: (theme) => `0 1px 4px ${alpha(theme.palette.secondary.main, 0.18)}`,
                                background: (theme) => theme.palette.mode === 'dark' ? undefined : '#00bcd4',
                                color: (theme) => theme.palette.mode === 'dark' ? undefined : '#fff',
                            }
                        }}>
                            <FavoriteIcon />
                        </Badge>
                    </IconButton>
                    <IconButton
                        size="large"
                        component={Link}
                        to="/cart"
                        aria-label={`show ${cartItems.length} cart items`}
                        sx={{
                            color: (theme) => theme.palette.text.primary,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                ? '0 2px 8px rgba(0,229,255,0.10)'
                                : '0 2px 8px rgba(25,118,210,0.06)',
                            '&:hover': {
                                transform: 'translateY(-2px) scale(1.08)',
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 4px 16px #00e5ff44'
                                    : '0 4px 16px #1976d244',
                            },
                        }}
                    >
                        <Badge badgeContent={cartItems.length} color="primary" sx={{
                            '& .MuiBadge-badge': {
                                fontWeight: 600,
                                fontSize: 13,
                                boxShadow: (theme) => `0 1px 4px ${alpha(theme.palette.primary.main, 0.18)}`,
                                background: (theme) => theme.palette.mode === 'dark' ? undefined : '#1976d2',
                                color: (theme) => theme.palette.mode === 'dark' ? undefined : '#fff',
                            }
                        }}>
                            <ShoppingCartIcon />
                        </Badge>
                    </IconButton>
                    <IconButton
                        size="large"
                        edge="end"
                        aria-label="account of current user"
                        component={Link}
                        to={isAuthenticated ? "/account" : "/login"}
                        sx={{
                            color: (theme) => theme.palette.text.primary,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                                ? '0 2px 8px rgba(0,229,255,0.10)'
                                : '0 2px 8px rgba(25,118,210,0.06)',
                            '&:hover': {
                                transform: 'translateY(-2px) scale(1.08)',
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 4px 16px #00e5ff44'
                                    : '0 4px 16px #1976d244',
                            },
                        }}
                    >
                        <AccountCircle />
                    </IconButton>
                </Box>
            </Toolbar>
            {isMobile && showSearch && (
                <Box sx={{ p: 1 }}>
                    <Search sx={{
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 2px 8px rgba(0,229,255,0.08)'
                            : '0 2px 8px rgba(25,118,210,0.04)',
                        border: (theme) => theme.palette.mode === 'dark'
                            ? `1.5px solid ${alpha(theme.palette.primary.main, 0.10)}`
                            : `1.5px solid ${alpha(theme.palette.grey[400], 0.25)}`,
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.default, 0.08)
                            : 'rgba(240, 245, 255, 0.95)',
                        transition: 'box-shadow 0.2s, border 0.2s',
                    }}>
                        <SearchIconWrapper>
                            <SearchIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Search for products, brands and more"
                            inputProps={{ 'aria-label': 'search', style: { color: 'inherit' } }}
                            sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#1a237e' }}
                            onKeyDown={handleSearch}
                            autoFocus
                        />
                    </Search>
                </Box>
            )}
        </AppBar>
    );
}
